import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { routeCassetteChock } from "@/setup/routes/catalog.routes";
import { autocompleteOptionColor } from "@/utils/theme";
import { dxtToLocalServerTime, ErrorModal } from "@dexteel/mesf-core";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Autocomplete,
	Box,
	CircularProgress,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { Link } from "react-router-dom";
import { CassetteChock } from "../models/CassetteChock";
import { getCassetteChocks } from "../repositories/CassetteChockRepository";
import { NewCassetteChockDialog } from "./NewCassetteChockDialog";

type Props = {
	id?: string;
	label: string;
	value: number | null;
	onChange?: Function;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
};

const useCassetteChocks = (
	partId?: number,
	onError?: (error: Error) => void,
) => {
	return useQuery<CassetteChock[], Error>({
		queryKey: ["cassetteChocks", partId],
		queryFn: ({ signal }) => getCassetteChocks({ partId }, signal),
		onError: onError,
	});
};

export const CassetteChockSelector = ({
	id,
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
}: Props) => {
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [selectedValue, setSelectedValue] = useState<CassetteChock | null>(
		null,
	);
	const [showNewCassetteChockDialog, setShowNewCassetteChockDialog] =
		useState(false);

	const { data: cassetteChocks, isLoading } = useCassetteChocks(
		undefined,
		(error) => setError(error.message),
	);

	const selectedChock =
		cassetteChocks?.find((chock) => chock.PartId === value) || null;

	const inUse = (props: CassetteChock | null) => {
		if (props?.InUse) return true;
		return false;
	};

	const handleConfirmationClose = (result?: string) => {
		setMessage("");
		if (result === "OK") {
			onChange(selectedValue?.PartId ?? null);
			setSelectedValue(null);
			return;
		}
	};

	return (
		<>
			<Autocomplete
				id={id}
				clearOnEscape
				autoHighlight
				options={cassetteChocks || []}
				getOptionLabel={(option) => option.PartName}
				renderOption={(props, option) => {
					return (
						<Box
							{...props}
							component="li"
							bgcolor={inUse(option) ? autocompleteOptionColor : ""}
						>
							<Box
								sx={{
									width: "100%",
									height: "100%",
									boxSizing: "border-box",
								}}
							>
								<Box component="li">
									<Typography>
										{option.PartName}
										<i>
											{inUse(option)
												? option?.PartId == value
													? " (Actual) "
													: " (In Use)"
												: ""}
										</i>
									</Typography>
								</Box>
								<Box
									style={{
										display: "flex",
										width: "100%",
										gap: 10,
									}}
								>
									<Typography variant="caption" color="textSecondary">
										Rebuild Date:{" "}
										{dxtToLocalServerTime(
											option?.RebuildTimestamp,
											"MM/dd/yy",
										) || "-"}
									</Typography>
									<Typography variant="caption" color="textSecondary">
										Bearing Change Date:{" "}
										{dxtToLocalServerTime(
											option?.BearingChangeTimestamp,
											"MM/dd/yy",
										) || "-"}
									</Typography>
								</Box>
							</Box>
						</Box>
					);
				}}
				isOptionEqualToValue={(option, value) => option.PartId === value.PartId}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						variant="outlined"
						fullWidth
						size="small"
						error={!!fieldError}
						helperText={fieldError?.message}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										<>
											{params.InputProps.endAdornment}
											{!disabled && (
												<IconButton
													onClick={(e) => {
														e.stopPropagation();
														setShowNewCassetteChockDialog(true);
													}}
													size="small"
													title="Add new cassette chock"
												>
													<AddIcon fontSize="small" />
												</IconButton>
											)}
											{value ? (
												<IconButton
													component={Link}
													to={`${routeCassetteChock}/${value}`}
													target="_blank"
													size="small"
													title="See in cassette chocks"
												>
													<VisibilityIcon fontSize="small" />
												</IconButton>
											) : null}
										</>
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedChock}
				onChange={(event, newValue) => {
					if (inUse(newValue)) {
						setMessage(
							`The cassette chock ${newValue?.PartName} is already in use by another cassette, do you want to disassemble it?`,
						);
						setSelectedValue(newValue);
					} else onChange(newValue?.PartId ?? null);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading cassettes"
			/>
			<ConfirmationDialogRaw
				title="CONFIRMATION"
				message={message}
				open={message !== ""}
				onClose={handleConfirmationClose}
			/>
			<NewCassetteChockDialog
				show={showNewCassetteChockDialog}
				onHide={(newPartId?: number) => {
					setShowNewCassetteChockDialog(false);
					if (newPartId) {
						onChange(newPartId);
					}
				}}
			/>
		</>
	);
};
